import moment from 'moment';

import { NgbActiveModal }   from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import {
  Component,
  Input,
  OnInit
} from '@angular/core';

import { BookingDiaryService }       from './booking-diary.service';
import { BookingDiaryMechanicModel } from './typings/booking-diary.mechanic';
import { CompanyService }            from '../companies/company.service';
import { DropdownOptionModel }       from '../shared/components/dropdown/typings/dropdown-option';

import {
  BookingDiaryBookingDraftModel,
  BookingDiaryNewCustomerModel,
  BookingDiaryNewVehicleModel
} from './typings/booking-diary.draft';

import {
  BookingDiaryCustomerModel,
  BookingDiarySearchResultModel,
  BookingDiaryVehicleModel,
  BookingDiaryVehicleSearchModel
} from './typings/booking-diary.customer';

import {
  DATE_FORMAT,
  formatClockLabel,
  MIN_HOURS,
  SLOT_MINUTES
} from './booking-diary.layout';

import {
  mechanicOptionsFor,
  timeOptionsBetween,
  vehicleOptionsFor
} from './booking-diary.options';

const NEW_VEHICLE_ID   : string = 'new';
const SEARCH_MAX_ROWS  : number = 8;
const SEARCH_MIN_CHARS : number = 2;

// A booking taken over the phone: customer, vehicle, job, length and start, saved onto the slot
@Component({
  selector   : 'wor-booking-diary-booking-modal',
  template: require('./booking-diary.booking-modal.component.html')
})
export class BookingDiaryBookingModalComponent implements OnInit {
  @Input() date              : string;

  // the diary holds the day's bookings, so it answers whether a time is free
  @Input() isSlotFree        : ( mechanicId : string, startMinutes : number, endMinutes : number ) => boolean;
  @Input() mechanicId        : string;
  @Input() mechanics         : Array<BookingDiaryMechanicModel> = [];
  @Input() shopClosesMinutes : number;
  @Input() shopOpensMinutes  : number;
  @Input() startMinutes      : number;

  customer          : BookingDiaryCustomerModel       = null;
  customerQuery     : string                          = '';
  description       : string                          = '';
  error             : string                          = null;
  hasSearched       : boolean                         = false;
  hours             : number                          = 1;
  isLoadingVehicles : boolean                         = false;
  isNewCustomer     : boolean                         = false;
  isSaving          : boolean                         = false;
  mechanicOptions   : Array<DropdownOptionModel>      = [];
  minHours          : number                          = MIN_HOURS;
  regoLabel         : string                          = '';
  resultCount       : number                          = 0;
  suggestedStart    : number                          = null;
  timeOptions       : Array<DropdownOptionModel>      = [];
  vehicleId         : string                          = null;
  vehicleOptions    : Array<DropdownOptionModel>      = [];
  vehicles          : Array<BookingDiaryVehicleModel> = [];

  newCustomer : BookingDiaryNewCustomerModel = {
    company_name: '',
    first_name  : '',
    last_name   : '',
    mobile      : ''
  };

  newVehicle : BookingDiaryNewVehicleModel = {
    color       : '',
    make        : '',
    model       : '',
    plate_number: ''
  };

  // customers and vehicles are searched together, so a rego finds the customer as readily as a name
  searchFn = ( typed : string ) : Promise<Array<BookingDiarySearchResultModel>> => {
    const query = ( typed || '' ).trim();

    if ( query.length < SEARCH_MIN_CHARS ) {
      return Promise.resolve([]);
    }

    return Promise.all([
      this.bookingDiaryService.searchCustomers(query),
      this.bookingDiaryService.searchVehicles(query)
    ])
      .then(([ customers, vehicles ]) => [
        ...customers.slice(0, SEARCH_MAX_ROWS).map(( customer, index ) => ( {
          customer,
          heading: index === 0 && vehicles.length
            ? 'CUSTOMER_SPACE.CUSTOMERS'
            : undefined,
          label  : customer.display_name,
          meta   : [ customer.phone, customer.suburb ].filter(part => !!part).join(' · ')
        } )),
        ...vehicles.slice(0, SEARCH_MAX_ROWS).map(( vehicle, index ) => ( {
          heading: index === 0
            ? 'VEHICLE_SPACE.VEHICLES'
            : undefined,
          label  : [ vehicle.make, vehicle.model ].filter(part => !!part).join(' '),
          meta   : vehicle.display_name || '',
          plate  : vehicle.plate_number,
          vehicle
        } ))
      ])
      .catch(() => []);
  }

  // a vehicle chosen from the search is applied once its owner's vehicles have loaded
  private pendingVehicleId : string = null;

  constructor (
    public activeModal          : NgbActiveModal,
    private bookingDiaryService : BookingDiaryService,
    private companyService      : CompanyService,
    private translateService    : TranslateService
  ) {}

  ngOnInit () : void {
    this.regoLabel = this.companyService.labels.rego;

    this.mechanicOptions = mechanicOptionsFor(this.mechanics, this.translateService);
    this.timeOptions     = timeOptionsBetween(this.shopOpensMinutes, this.shopClosesMinutes);

    this.buildVehicleOptions();
  }

  cancelNewCustomer () : void {
    this.isNewCustomer = false;

    this.buildVehicleOptions();
    this.clearError();
  }

  get canSave () : boolean {
    return this.hasCustomer && this.hours >= this.minHours && !this.isSaving;
  }

  clearCustomer () : void {
    this.customer      = null;
    this.customerQuery = '';
    this.hasSearched   = false;
    this.resultCount   = 0;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
  }

  clearError () : void {
    this.error          = null;
    this.suggestedStart = null;
  }

  get customerSummary () : string {
    return [ this.customer && this.customer.phone, this.vehicleSummary ].filter(part => !!part).join(' · ');
  }

  get dateLabel () : string {
    return moment(this.date, DATE_FORMAT).format('dddd, D MMMM YYYY');
  }

  get durationMinutes () : number {
    return Math.round(( this.hours || 0 ) * 60);
  }

  get endMinutes () : number {
    return this.startMinutes + this.durationMinutes;
  }

  get hasCustomer () : boolean {
    return this.isNewCustomer
      ? this.isNewCustomerValid
      : !!this.customer;
  }

  get hasCustomerContext () : boolean {
    return !!this.customer || this.isNewCustomer;
  }

  get isNewCustomerValid () : boolean {
    const typed = this.trimmed(this.newCustomer);

    return !!typed.company_name || ( !!typed.first_name && !!typed.last_name );
  }

  get isNewVehicle () : boolean {
    return this.vehicleId === NEW_VEHICLE_ID;
  }

  get mechanicName () : string {
    const mechanic = this.mechanics.find(item => item.id === this.mechanicId);

    return mechanic
      ? mechanic.name
      : this.translateService.instant('JS_SPACE.BOOKINGS.UNASSIGNED');
  }

  onResults ( results : Array<BookingDiarySearchResultModel> ) : void {
    this.hasSearched = true;
    this.resultCount = ( results || [] ).length;
  }

  onVehicleChange ( value : string | number ) : void {
    this.vehicleId = value === null || value === undefined
      ? null
      : String(value);

    this.clearError();
  }

  // a booking past closing or into another is refused, and the next start that fits is offered
  save () : void {
    this.clearError();

    if ( !this.canSave ) {
      return;
    }

    if ( this.endMinutes > this.shopClosesMinutes ) {
      this.error          = this.translateService.instant('BOOKING_SPACE.DIARY.PAST_CLOSING');
      this.suggestedStart = this.nextFreeStart();

      return;
    }

    if ( this.isSlotFree && !this.isSlotFree(this.mechanicId, this.startMinutes, this.endMinutes) ) {
      this.error          = this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN');
      this.suggestedStart = this.nextFreeStart();

      return;
    }

    const draft = this.buildDraft();

    this.isSaving = true;

    this.isSlotStillFree()
      .then(isFree => isFree
        ? this.bookingDiaryService.createBooking(draft)
        : Promise.reject(this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN')))
      .then(() => {
        this.activeModal.close(draft);
      })
      .catch(err => {
        this.error    = this.errorMessage(err);
        this.isSaving = false;
      });
  }

  get saveHint () : string {
    if ( !this.hasCustomer ) {
      return this.translateService.instant(this.isNewCustomer
        ? 'BOOKING_SPACE.DIARY.ENTER_NAME'
        : 'BOOKING_SPACE.DIARY.CHOOSE_CUSTOMER_FIRST');
    }

    return this.hours >= this.minHours
      ? ''
      : this.translateService.instant('BOOKING_SPACE.DIARY.ENTER_HOURS');
  }

  selectCustomer ( customer : BookingDiaryCustomerModel ) : void {
    this.customer      = customer;
    this.customerQuery = '';
    this.isNewCustomer = false;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
    this.loadVehicles(customer);
  }

  selectResult ( result : BookingDiarySearchResultModel ) : void {
    if ( result.customer ) {
      this.selectCustomer(result.customer);

      return;
    }

    if ( result.vehicle ) {
      this.selectVehicleResult(result.vehicle);
    }
  }

  // a rego hit picks the owner and, once their vehicles arrive, the vehicle itself
  selectVehicleResult ( vehicle : BookingDiaryVehicleSearchModel ) : void {
    this.pendingVehicleId = vehicle.id;

    this.selectCustomer({
      display_name: vehicle.display_name || '',
      id          : vehicle.customer_id
    });
  }

  // whatever was typed into the search is the best guess at the name, so it seeds first and last
  startNewCustomer () : void {
    const words = ( this.customerQuery || '' ).trim().split(/\s+/).filter(word => !!word);

    this.newCustomer.first_name = words[0] || '';
    this.newCustomer.last_name  = words.slice(1).join(' ');

    this.isNewCustomer = true;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
  }

  get suggestedStartLabel () : string {
    return formatClockLabel(this.suggestedStart);
  }

  get suggestionDetail () : string {
    return this.translateService.instant('BOOKING_SPACE.DIARY.IS_FREE_AT', {
      mechanic: this.mechanicName,
      time    : `${ formatClockLabel(this.suggestedStart) } – ${ formatClockLabel(this.suggestedStart + this.durationMinutes) }`
    });
  }

  get summaryLabel () : string {
    return this.hours > 0
      ? `${ formatClockLabel(this.startMinutes) } – ${ formatClockLabel(this.endMinutes) } · ${ this.mechanicName }`
      : this.mechanicName;
  }

  useSuggestion () : void {
    this.startMinutes = this.suggestedStart;

    this.save();
  }

  get vehicleSummary () : string {
    if ( this.isLoadingVehicles ) {
      return this.translateService.instant('BOOKING_SPACE.DIARY.LOADING_VEHICLES');
    }

    if ( !this.vehicles.length ) {
      return '';
    }

    return this.vehicles.length === 1
      ? this.translateService.instant('BOOKING_SPACE.DIARY.VEHICLE_ON_FILE')
      : this.translateService.instant('BOOKING_SPACE.DIARY.VEHICLES_ON_FILE', { count: this.vehicles.length });
  }

  // a new vehicle with nothing typed into it is no vehicle, not a blank one
  private buildDraft () : BookingDiaryBookingDraftModel {
    const newVehicle    = this.trimmed(this.newVehicle);
    const hasNewVehicle = this.isNewVehicle && Object.keys(newVehicle).some(key => !!newVehicle[key]);

    return {
      customer    : this.isNewCustomer
        ? null
        : this.customer,
      date        : this.date,
      description : ( this.description || '' ).trim(),
      hours       : this.hours,
      mechanicId  : this.mechanicId || null,
      newCustomer : this.isNewCustomer
        ? this.trimmed(this.newCustomer)
        : null,
      newVehicle  : hasNewVehicle
        ? newVehicle
        : null,
      startMinutes: this.startMinutes,
      vehicle     : this.isNewVehicle
        ? null
        : this.vehicles.find(vehicle => vehicle.id === this.vehicleId) || null
    };
  }

  // the empty choice names the state it stands for, and typing a new vehicle in stays the last row
  private buildVehicleOptions () : void {
    if ( !this.hasCustomerContext ) {
      this.vehicleOptions = [ {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.CHOOSE_CUSTOMER_FIRST'),
        value: null
      } ];

      return;
    }

    if ( this.isLoadingVehicles ) {
      this.vehicleOptions = [ {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.LOADING_VEHICLES'),
        value: null
      } ];

      return;
    }

    this.vehicleOptions = [
      ...vehicleOptionsFor(this.vehicles, this.translateService),
      {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.ADD_VEHICLE'),
        value: NEW_VEHICLE_ID
      }
    ];
  }

  private errorMessage ( err : any ) : string {
    if ( typeof err === 'string' ) {
      return err;
    }

    return ( err && err.message )
      || this.translateService.instant('BOOKING_SPACE.DIARY.SAVE_FAILED');
  }

  // the diary may be stale; if the re-read fails, the check the diary already passed stands
  private isSlotStillFree () : Promise<boolean> {
    if ( !this.mechanicId ) {
      return Promise.resolve(true);
    }

    return this.bookingDiaryService.isSlotFree(this.date, this.mechanicId, this.startMinutes, this.endMinutes)
      .catch(() => true);
  }

  // a customer with one vehicle almost always means that vehicle, so it is chosen
  private loadVehicles ( customer : BookingDiaryCustomerModel ) : void {
    this.isLoadingVehicles = true;

    this.bookingDiaryService.getCustomerVehicles(customer.id)
      .then(vehicles => {
        if ( this.customer !== customer ) {
          return;
        }

        this.vehicles = vehicles || [];

        const pending = this.pendingVehicleId && this.vehicles.some(vehicle => vehicle.id === this.pendingVehicleId)
          ? this.pendingVehicleId
          : null;

        this.vehicleId = pending || ( this.vehicles.length === 1
          ? this.vehicles[0].id
          : null );

        this.pendingVehicleId = null;
      })
      .catch(() => {
        this.vehicles = [];
      })
      .then(() => {
        this.isLoadingVehicles = false;

        this.buildVehicleOptions();
      });
  }

  // the next start today that fits before closing and clears the mechanic; later slots first
  private nextFreeStart () : number {
    const duration = this.durationMinutes;
    const latest   = this.shopClosesMinutes - duration;

    const candidates : Array<number> = [];

    for ( let start = this.startMinutes + SLOT_MINUTES; start <= latest; start += SLOT_MINUTES ) {
      candidates.push(start);
    }

    for ( let start = this.shopOpensMinutes; start < this.startMinutes && start <= latest; start += SLOT_MINUTES ) {
      candidates.push(start);
    }

    const found = candidates.find(start => !this.isSlotFree || this.isSlotFree(this.mechanicId, start, start + duration));

    return found === undefined
      ? null
      : found;
  }

  private trimmed<T> ( fields : T ) : T {
    const copy : Record<string, string> = {};

    Object.keys(fields).forEach(key => {
      copy[key] = ( fields[key] || '' ).trim();
    });

    return copy as unknown as T;
  }
}
